/**
 * @module registration/creative/components/substep2_4
 */

import React, { Component, PropTypes } from 'react';
import { Link } from 'react-router';
import { SubmissionError } from 'redux-form';
import partial from 'lodash/partial';
import pick from 'lodash/pick';
import classnames from 'classnames';
import { updateUserRequest, profileDefaults } from 'common/ducks/user';
import { submitRegistration } from 'registration/creative/ducks';
import ReviewForm from 'registration/creative/components/review_form';
import { trackGoogle } from 'common/tracking';
import { GROUP_ICON } from 'registration/creative/components/work_samples';

/**
 * Substep 2.4 of creative registration.
 * @author Nathan Ozelim <nathan@startupfoundry.com>
 */
class Substep2_4 extends Component {
  constructor(props) {
    super(props);

    this.state = {done: false};

    this.handleSubmit = this.handleSubmit.bind(this);
    this.goToVerificationSubStep = this.goToVerificationSubStep.bind(this);
  }

  renderDone() {
    if (this.state.newStatus === 'closed') {
      return (
        <div className="done">
          <h3>What happens next?</h3>
          <p>
            Because you have not asked to work in any new categories, we have not submitted
            your application at this time. If you decide that you want to work in new
            categories, you can request to do so at any time by visiting the verification
            tab of <Link to="/account/id-verification/">your account</Link>.
          </p>
        </div>
      );
    }

    return (
      <div className="done">
        <h2 className="text-success">
          <i className="fa fa-check-circle fa-2x" /><br/> Application Complete!
        </h2>

        <h3>What happens next?</h3>

        <p>
          We'll review your application, including the work samples you submitted.
          <strong> This review process typically takes 7 to 14 days.</strong>
        </p>
        <p>
          You will not be able to submit work to any projects until your application
          has been approved.
        </p>
        <p>
          We'll email you after we review your application. Please be patient and do not
          contact our team unless more than 21 days have passed after you submitted your
          application.
        </p>
        <p>
          You can always see the current status of your application in the verification
          tab of <Link to="/account/id-verification/">your account</Link>.
        </p>

        <p>
          In the meantime, visit our FAQs for creatives to learn more about our rules,
          policies, and working on crowdspring.
        </p>

        <Link className="btn btn-tertiary btn-block" to="/help/">
          Visit FAQs
        </Link>
      </div>
    );
  }

  renderContact() {
    const { user, goToSubstep, registrationSubmittedAt, registrationReopenedAt } = this.props;
    const countryDef = user.countries.find(c => c.field === user.profile_data.country);
    const countryLabel = countryDef? countryDef.label : user.profile_data.country.toUpperCase();
    const timezoneDef = user.timezones.find(t => t.field === user.profile_data.user_timezone);
    const timezoneLabel = timezoneDef? timezoneDef.label : user.profile_data.user_timezone;

    if (registrationSubmittedAt || registrationReopenedAt) {
      return null;
    }

    return (
      <section className="review-item contact">
        <h3>
          Contact Info
          <button className="btn btn-icon-only" onClick={partial(goToSubstep, 'settings')}>
            <i className="fa fa-pencil"></i>
          </button>
        </h3>

        <div className="form-group">
          <label>First name</label>
          <div className="answer">{user.profile_data.first_name}</div>
        </div>

        <div className="form-group">
          <label>Last name</label>
          <div className="answer">{user.profile_data.last_name}</div>
        </div>

        <div className="form-group">
          <label>Date of birth</label>
          <div className="answer">{user.profile_data.date_of_birth}</div>
        </div>

        <div className="form-group">
          <label>Address</label>
          <div className="answer">{user.profile_data.address_line_1}</div>
        </div>

        <div className="form-group">
          <label>Address 2</label>
          <div className="answer">{user.profile_data.address_line_2}</div>
        </div>

        <div className="form-group">
          <label>City/Town</label>
          <div className="answer">{user.profile_data.city}</div>
        </div>

        <div className="form-group">
          <label>State/County/Province</label>
          <div className="answer">{user.profile_data.state}</div>
        </div>

        <div className="form-group">
          <label>ZIP/Postal code</label>
          <div className="answer">{user.profile_data.postal_code}</div>
        </div>

        <div className="form-group">
          <label>Country</label>
          <div className="answer">{ countryLabel }</div>
        </div>

        <div className="form-group">
          <label>Phone</label>
          <div className="answer">{user.profile_data.phone_number}</div>
        </div>

        <div className="form-group">
          <label>Timezone</label>
          <div className="answer">{ timezoneLabel }</div>
        </div>
      </section>
    );
  }

  goToVerificationSubStep() {
    const { user, goToSubstep, updateMobilePhone } = this.props
    /* If the smartphone method was used, re-send the magic link so that ID photo and selfie
       can be replaced. */
    if (user.mobileUpdatedSmart) {
      updateMobilePhone(user.profile_data.mobile_number, true, partial(goToSubstep, 'verify'))
    } else {
      goToSubstep('verify')
    }
  }

  renderVerification() {
    const { idValidated, goToSubstep, idPhoto, selfie, user } = this.props;

    if (idValidated) {
      return null;
    }

    return (
      <section className="review-item verification">
        <h3>
          ID Verification

          <button className="btn btn-icon-only" disabled={user.updatingMobile}
                  onClick={this.goToVerificationSubStep}>
            <i className="fa fa-pencil"></i>
          </button>
        </h3>

        {user.updatingMobile &&
        <div className="callout primary">
          We are sending you a new magic link via SMS so that you can replace your
          ID photo and selfie. Please wait...
        </div>}

        {user.error &&
        <div className="callout alert">{user.error}</div>}

        <div className="row">
          <div className="columns small-8">
            <img src={idPhoto}/>
          </div>
          <div className="columns small-8">
            <img src={selfie}/>
          </div>
        </div>

      </section>
    );
  }

  renderProfile() {
    const { user, goToSubstep, registrationSubmittedAt, registrationReopenedAt } = this.props

    if (registrationSubmittedAt || registrationReopenedAt) {
      return null;
    }

    return (
      <section className="review-item profile">
        <h3>
          Profile
          <button className="btn btn-icon-only" onClick={partial(goToSubstep, 'profile')}>
            <i className="fa fa-pencil"></i>
          </button>
        </h3>

        <div className="form-group">
          <label>Avatar</label>
          <div>
            <img src={user.profile_data.avatar}/>
          </div>
        </div>
      </section>)
  }

  renderWorkSamples() {
    const {
      workSamples, goToSubstep, sampleGroups, registrationSubmittedAt, idValidated
    } = this.props;

    if (registrationSubmittedAt && !idValidated) {
      return null;
    }

    return (
      <section className="review-item work-samples">
        <h3>
          Work Samples
          <button className="btn btn-icon-only" onClick={partial(goToSubstep, 'profile')}>
            <i className="fa fa-pencil"></i>
          </button>
        </h3>
        <div className="groups">
        { sampleGroups.map((group) => {
            const active = workSamples[group.id] && Object.keys(workSamples[group.id]).length > 0;
            const sampleType = group.subcategories[0].entry_type;
            const iconName = GROUP_ICON[group.name];
            const icon = require(`assets/images/icons/pap/PAP_cat_icon_${iconName}.svg`);

            return (
              <div key={group.id} className="row group">
                <div className="columns medium-6 large-5 small-16">
                  <div className={classnames("tile", { active })}>
                    <i className="icon" dangerouslySetInnerHTML={{ __html: icon }} />
                    <h4>{ group.name }</h4>
                  </div>
                </div>
                <div className="columns medium-10 large-11 small-16">
                  { active?
                  <div className="samples">
                    { sampleType === 'image'?
                    <div className="images">
                      { Object.values(workSamples[group.id]).map((s, i) => <img key={i} src={s} />) }
                    </div> :
                    <div>
                      <h5>Required questions answered.</h5>
                    </div> }
                  </div> :
                  <div className="custom-description">
                    { sampleType === 'image'? 'No samples submitted.' : 'No questions answered.' }
                  </div> }
                </div>
              </div>
            );
        }) }
        </div>
      </section>
    );
  }

  handleSubmit(formData) {
    const userId = this.props.user.profile_data.id
    const data = pick(formData, ['receive_communication', 'accepted_terms_of_use'])

    return submitRegistration(userId)
      .then((res) => {
        return updateUserRequest(userId, data).then(() => {
          trackGoogle("registration", "submitted application", "creative");
          this.setState({ done: true, newStatus: res.data.status });
        });
      }).catch((res) => {
        /* field-specific errors */
        const data = res.data;
        if (data) {
          for (const field of Object.keys(data)) {
            if (data[field]) throw new SubmissionError({ [field]: data[field][0] });
          }
        }
        /* global error */
        throw new SubmissionError({ _error: 'Unknown error. Please try again.' });
      });
  }

  render() {
    const { done } = this.state

    return (
      <div className="row align-center substep2-4">
        <div className="columns small-16 medium-12 large-8">
          { done? this.renderDone():
          <div>
            <h2>Review</h2>

            { this.renderContact() }

            { this.renderVerification() }

            { this.renderProfile() }

            { this.renderWorkSamples() }

            <ReviewForm
              onSubmit={this.handleSubmit}
              initialValues={profileDefaults}
            />
          </div> }
        </div>
      </div>
    );
  }
}

Substep2_4.propTypes = {
  user: PropTypes.object,
  workSamples: PropTypes.object,
  sampleGroups: PropTypes.array.isRequired,
  goToSubstep: PropTypes.func,
  updateMobilePhone: PropTypes.func,
  idPhoto: PropTypes.string,
  selfie: PropTypes.string,
  idValidated: PropTypes.bool,
  registrationSubmittedAt: PropTypes.string,
  registrationReopenedAt: PropTypes.string,
};

export default Substep2_4;



// WEBPACK FOOTER //
// ./registration/creative/components/substep2_4.jsx